import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { functions } from '../../../firebase';
import type {
  UnifiedDevice,
  UnifiedDeviceMode,
} from '../../../../functions/src/monitoring/unified/model';

export type { UnifiedDevice };
export type DeviceScope = { orgId: string; sensorId: string };
export const heartbeatQueryKey = ['unified-heartbeats'] as const;
export const modeHistoryQueryKey = ['unified-device-mode-history'] as const;

const timestamp = z.number().finite().nonnegative();
// Runtime validation stays inside src; server ownership is checked through the type.
const modeValue = z.enum(['live', 'test', 'not_in_use']);
const modeSchema: z.ZodType<UnifiedDeviceMode> = z.object({
  orgId: z.string(),
  sensorId: z.string(),
  mode: modeValue,
  version: z.number().int().nonnegative(),
  updatedAt: timestamp.nullable(),
  updatedBy: z.string().nullable(),
});
const deviceSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  sensorId: z.string(),
  orgTitle: z.string(),
  sensorTitle: z.string(),
  sensorType: z.string(),
  deviceId: z.string(),
  detailPath: z.string(),
  mode: modeSchema,
  heartbeat: z.object({
    status: z.enum(['on_time', 'overdue', 'never_seen', 'unknown', 'paused', 'unsupported']),
    lastDataAt: timestamp.nullable(),
    expectedAfter: timestamp.nullable(),
    maxAgeMinutes: z.number().finite().nonnegative().nullable(),
    ruleId: z.string().nullable(),
    ruleMode: modeValue.nullable().optional(),
    evaluatedAt: timestamp,
  }),
  updatedAt: timestamp,
  openIncidentCount: z.number().int().nonnegative(),
  alertSetup: z.object({
    status: z.enum(['legacy', 'prepared', 'active', 'attention']),
    ruleCount: z.number().int().nonnegative(),
    blockerCount: z.number().int().nonnegative(),
    activatedAt: timestamp.nullable(),
  }).optional(),
});
const overviewSchema = z.object({
  devices: z.array(deviceSchema),
  nextCursor: z.string().min(1).nullable(),
});
const historySchema = z.object({
  history: z.array(
    z.object({
      version: z.number().int().nonnegative(),
      mode: modeValue,
      previousMode: modeValue,
      updatedAt: timestamp,
      updatedBy: z.string(),
      note: z.string(),
    })
  ),
  nextCursor: z.string().min(1).nullable(),
});

export function modeSaveError(error: unknown) {
  const code = (error as { code?: string })?.code || '';
  if (code.includes('aborted') || code.includes('conflict') || code === '409') {
    return 'Enhedens driftsform er ændret af en anden. Opdatér, kontrollér den nye værdi og prøv igen.';
  }
  if (code.includes('permission-denied') || code === '403') {
    return 'Du har ikke adgang til at ændre enhedens driftsform.';
  }
  return 'Driftsformen kunne ikke gemmes. Ingen ændring er bekræftet. Prøv igen.';
}

async function call(requestType: string, payload: Record<string, unknown>) {
  const response = await functions.httpsCallable('settings')({ requestType, payload });
  const status = response.data?.httpErrorCode?.status;
  if (status && status !== 200) {
    throw Object.assign(new Error('Overvågningen kunne ikke opdateres.'), { code: String(status) });
  }
  return response.data;
}

export async function fetchDeviceHeartbeats(scope?: DeviceScope): Promise<UnifiedDevice[]> {
  const devices = new Map<string, UnifiedDevice>();
  const seenCursors = new Set<string>();
  let cursor: string | null = null;
  do {
    const result = overviewSchema.safeParse(
      await call('GET_UNIFIED_HEARTBEATS', { ...scope, cursor })
    );
    if (!result.success) throw new Error('Datakontrollen returnerede et ugyldigt svar. Prøv igen.');
    result.data.devices.forEach((device) => {
      if (device.mode.orgId !== device.orgId || device.mode.sensorId !== device.sensorId) {
        throw new Error('Enhedens driftsform kunne ikke bekræftes. Prøv igen.');
      }
      if (scope && (device.orgId !== scope.orgId || device.sensorId !== scope.sensorId)) {
        throw new Error('Datakontrollen returnerede en anden enhed. Prøv igen.');
      }
      devices.set(`${device.orgId}/${device.sensorId}`, device);
    });
    cursor = result.data.nextCursor;
    if (cursor && seenCursors.has(cursor)) throw new Error('Enhedslisten blev ikke færdigindlæst.');
    if (cursor) seenCursors.add(cursor);
  } while (cursor);
  return Array.from(devices.values());
}

export function useDeviceHeartbeats(scope?: DeviceScope, enabled = true) {
  return useQuery({
    queryKey: [...heartbeatQueryKey, scope?.orgId ?? null, scope?.sensorId ?? null],
    queryFn: () => fetchDeviceHeartbeats(scope),
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export type SaveDeviceMode = DeviceScope & {
  mode: UnifiedDevice['mode']['mode'];
  expectedVersion: number;
  note: string;
};

export async function saveDeviceMode(command: SaveDeviceMode) {
  const response = await call('SAVE_UNIFIED_DEVICE_MODE', command);
  const result = modeSchema.safeParse(response);
  if (
    !result.success ||
    result.data.orgId !== command.orgId ||
    result.data.sensorId !== command.sensorId ||
    result.data.mode !== command.mode ||
    result.data.version <= command.expectedVersion
  ) {
    throw new Error('Ændringen kunne ikke bekræftes. Opdatér enheden, før du prøver igen.');
  }
  return result.data;
}

export function useSaveDeviceMode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: saveDeviceMode,
    onSuccess: async () => {
      await client.cancelQueries({ queryKey: heartbeatQueryKey });
      // The count and heartbeat depend on mode. Refresh them together instead of
      // attaching a new mode to the previous mode's incident count or deadline.
      await Promise.all([
        client.invalidateQueries({ queryKey: heartbeatQueryKey }),
        client.invalidateQueries({ queryKey: ['unified-alerts'] }),
      ]);
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: heartbeatQueryKey });
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: modeHistoryQueryKey });
    },
  });
}

export function useDeviceModeHistory(scope: DeviceScope, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...modeHistoryQueryKey, scope.orgId, scope.sensorId],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const result = historySchema.safeParse(
        await call('GET_UNIFIED_DEVICE_MODE_HISTORY', { ...scope, cursor: pageParam })
      );
      if (!result.success) throw new Error('Historikken kunne ikke læses. Prøv igen.');
      return result.data;
    },
    getNextPageParam: (last) => last.nextCursor || undefined,
    enabled,
    staleTime: 60_000,
  });
}
